import { Box, Group, NumberInput, Select, Space } from '@mantine/core';
import { useContext, useEffect, useState } from 'react';
import BookingStateItem from '../BookingStateItem';
import { EventsContext } from '../../../Service/Context/EventsContext';
import { ThemeText } from '../../../Atoms/ThemeText';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { GERMAN_MONTHS, getDateRangeFromSelection, getIntervalInputValues } from './dateIntervalSelectionUtils';
import { MixpanelS } from '../../../Service/MixpanelS';
import { ConstantS } from '../../../Service/ConstantS';

const DateIntervalSelection = () => {
  const { filter, updateFilter } = useContext(EventsContext);
  const { interval, from, to } = filter;
  const { sm } = useContext(WindowContext);
  const currentIntervalInputValues = getIntervalInputValues(interval, from, to);
  const [selectedYear, setSelectedYear] = useState(currentIntervalInputValues.year);
  const [selectedMonth, setSelectedMonth] = useState(currentIntervalInputValues.month);

  useEffect(() => {
    setSelectedYear(currentIntervalInputValues.year);
    setSelectedMonth(currentIntervalInputValues.month);
  }, [currentIntervalInputValues.month, currentIntervalInputValues.year]);

  useEffect(() => {
    if (selectedYear === currentIntervalInputValues.year && selectedMonth === currentIntervalInputValues.month) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      applyCustomInterval(selectedYear, selectedMonth);
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [currentIntervalInputValues.month, currentIntervalInputValues.year, selectedMonth, selectedYear]);

  const applyPresetInterval = (nextInterval: typeof interval) => {
    MixpanelS.track(ConstantS.TrackingEvents.EventsFilterDateIntervalChanged, { interval: nextInterval });
    updateFilter({ interval: nextInterval, from: undefined, to: undefined });
  };

  const applyCustomInterval = (yearValue: string, monthValue: string) => {
    const nextRange = getDateRangeFromSelection(yearValue, monthValue);

    if (!nextRange) {
      return;
    }

    updateFilter({
      interval: 'ALL_TIME',
      from: nextRange.from,
      to: nextRange.to,
    });
  };

  const handleYearChange = (value?: number) => {
    const nextYear = value === undefined ? '' : value.toString().replace(/\D/g, '').slice(0, 4);
    setSelectedYear(nextYear);
  };

  const handleMonthChange = (value: string) => {
    const nextMonth = value.slice(0, 12);
    setSelectedMonth(nextMonth);
    if (nextMonth && !selectedYear) {
      setSelectedYear(new Date().getFullYear().toString());
    }
  };

  return (
    <>
      <Space h="xl" />
      <ThemeText pl={10}>Datumsbereich</ThemeText>
      <Space h="xs" />
      <Box px={10} pb="xs">
        <Group noWrap spacing={1}>
          <Select
            searchable
            clearable
            variant="filled"
            value={selectedMonth}
            onChange={(value) => handleMonthChange(value ?? '')}
            placeholder="Monat"
            data={GERMAN_MONTHS}
            rightSection={selectedMonth ? undefined : <></>}
          />
          <NumberInput
            value={selectedYear ? Number(selectedYear) : undefined}
            variant="filled"
            onChange={handleYearChange}
            placeholder="Jahr"
            min={2017}
            max={new Date().getFullYear() + 10}
            sx={{ maxWidth: 85, minWidth: 85 }}
          />
        </Group>
      </Box>
      {sm && (
        <>
          <BookingStateItem
            label="Letztes Jahr"
            onClick={() => applyPresetInterval('LAST_YEAR')}
            active={interval === 'LAST_YEAR'}
          />
          <BookingStateItem
            label="Dieses Jahr"
            onClick={() => applyPresetInterval('CURRENT_YEAR')}
            active={interval === 'CURRENT_YEAR'}
          />
          <BookingStateItem
            label="Letzter Monat"
            onClick={() => applyPresetInterval('LAST_MONTH')}
            active={interval === 'LAST_MONTH'}
          />
        </>
      )}

      <BookingStateItem
        label="Dieser Monat"
        onClick={() => applyPresetInterval('CURRENT_MONTH')}
        active={interval === 'CURRENT_MONTH'}
      />
      <BookingStateItem
        label="Nächste Events"
        onClick={() => applyPresetInterval('FUTURE')}
        active={interval === 'FUTURE'}
      />
      <BookingStateItem
        label="Nächster Monat"
        onClick={() => applyPresetInterval('NEXT_MONTH')}
        active={interval === 'NEXT_MONTH'}
      />
    </>
  );
};

export default DateIntervalSelection;
